import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Typography, message } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, LinkOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { useAuthStore } from '@/store/authStore';
import assetAuditService, { type AuditReportSummaryRow } from '@/services/asset-audit.service';

const { Text } = Typography;

const STATUS_TAG: Record<string, string> = {
  open: 'blue',
  closed: 'default',
  cancelled: 'red',
};

const AssetAuditReportPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canView = Boolean(
    user?.is_super_admin || user?.permissions?.includes('asset.audit.view') || user?.permissions?.includes('asset.view')
  );
  const [exportId, setExportId] = useState<string | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['asset-audit', 'report-summary'],
    queryFn: () => assetAuditService.getReportSummary(),
    enabled: canView,
  });

  const cols: ColumnsType<AuditReportSummaryRow> = [
    { title: 'Cycle', dataIndex: 'name', key: 'n', ellipsis: true, width: 200 },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 's',
      width: 100,
      render: (s: string) => <Tag color={STATUS_TAG[s] || 'default'}>{s}</Tag>,
    },
    {
      title: 'Period',
      key: 'p',
      width: 200,
      render: (_: unknown, r) =>
        r.period_start || r.period_end
          ? `${r.period_start ? String(r.period_start).slice(0, 10) : '—'} – ${
              r.period_end ? String(r.period_end).slice(0, 10) : '—'
            }`
          : '—',
    },
    { title: 'Total lines', dataIndex: 'total', key: 't', width: 90, align: 'right' as const },
    { title: 'Pending', dataIndex: 'pending', key: 'pen', width: 90, align: 'right' as const },
    { title: 'Found', dataIndex: 'found', key: 'f', width: 80, align: 'right' as const },
    { title: 'Missing', dataIndex: 'missing', key: 'm', width: 80, align: 'right' as const },
    { title: 'Damaged', dataIndex: 'damaged', key: 'd', width: 80, align: 'right' as const },
    { title: 'Discrepancies (m + d)', dataIndex: 'discrepancy_count', key: 'x', width: 130, align: 'right' as const },
    { title: '% done (excl. pending left)', dataIndex: 'completion_pct', key: 'c', width: 140, align: 'right' as const,
      render: (n: number, r) => (r.total > 0 ? `${n}%` : '—'),
    },
    {
      title: 'Actions',
      key: 'a',
      width: 280,
      render: (_: unknown, r) => (
        <Space size="small" wrap>
          <Link to={`/asset/audit?cycle=${encodeURIComponent(r.cycle_id)}`}>
            <Button type="link" size="small" icon={<LinkOutlined />}>
              Open cycle
            </Button>
          </Link>
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            loading={exportId === r.cycle_id}
            onClick={async () => {
              setExportId(r.cycle_id);
              try {
                const safe = r.name.replace(/[^\w\-. ]+/g, '_').trim().replace(/\s+/g, '-').slice(0, 50);
                await assetAuditService.downloadDiscrepancyReport(
                  r.cycle_id,
                  `${safe || 'audit'}-discrepancies.csv`
                );
                message.success('CSV downloaded');
              } catch (e: any) {
                message.error(e?.message || 'Export failed');
              } finally {
                setExportId(null);
              }
            }}
          >
            Discrepancies CSV
          </Button>
        </Space>
      ),
    },
  ];

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <Card>Access denied. Need asset.audit.view or asset.view</Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 8 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/asset/audit')}>
        Back to audit
      </Button>
      <Card
        style={{ marginTop: 8 }}
        title="Audit report — cycle summary"
        extra={
          <Text type="secondary" style={{ maxWidth: 420, display: 'inline-block' }}>
            One row per cycle with counts. Use the main audit screen to run verifications. Discrepancy export lists missing + damaged
            only.
          </Text>
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            “% done” is the share of lines in a <strong>non-pending</strong> end state (found, missing, or damaged) of all lines in
            the cycle. Use it as a quick progress indicator, not a financial measure.
        </Text>
        <Table<AuditReportSummaryRow> rowKey="cycle_id" loading={isLoading} dataSource={rows} columns={cols} scroll={{ x: 1000 }} />
      </Card>
    </div>
  );
};

export default AssetAuditReportPage;
